import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import Dropdown from "../../components/Atomos/DropDown/Dropdown";
import TextArea from "../../components/Atomos/Inputs/TextArea";
import InvoicePdfPreview from "../../components/Atomos/PdfPreview/InvoicePdfPreview";
import {
  getAccountingFeesForYear,
  upsertInvoice,
  type BillingHistoryDoc,
  type DuesTier,
  type FirmInvoiceDoc,
  type NewInvoiceItem,
} from "../../Services/Billing";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import {
  getAccountingOfficeDetail,
  officeLegalName,
  toFeeContext,
} from "../../utils/accountingOfficeDetail";
import { generateAccountingInvoicePDFBlob, buildAccountingInvoiceSectionTitle } from "../../utils/generateAccountingInvoicePDF";
import { buildDefaultCoverLetterBody } from "../../utils/accountingCoverLetter";
import {
  AccountingAssessmentMergedNotice,
  AccountingInvoiceMergeStatusNote,
  AccountingModalCheckbox,
  AccountingModalError,
  AccountingModalFooter,
  AccountingModalFirmSubtitle,
  AccountingModalSectionTitle,
  AccountingModalSummaryLine,
  AccountingModalSummaryPanel,
  AccountingModalTabs,
  displayToIso,
  formatMoney,
  isoToDisplay,
  MEMBER_BALANCE_CREDIT_LABEL,
  MEMBERSHIP_BALANCE_LABEL,
  todayIso,
} from "./accountingModalUi";
import { deriveBillingStatus, formatBillingStatusLabel, isCancelledBillingDoc } from "../../utils/billingStatus";
import { getOfficeById } from "../../Services/Office";
import {
  recordInvoiceBalanceDeltaMovement,
  resolvePerformedBy,
} from "../../utils/accountingOfficeBalance";
import { useAuth } from "../../store/AuthContext";
import {
  feeFromSavedInvoiceTierRange,
  resolveCatalogTierRangeForFees,
  savedInvoiceTierRangeId,
} from "../../utils/billingYear";
import { fiscalYearFromIso } from "../../utils/dates";
import {
  FEE_TYPE_LABEL_CLASS,
  FIELD_VALUE_CLASS,
} from "./accountingFirmPanels";
import { useInvoiceAuditLogger } from "../../utils/invoiceAudit";
import {
  buildInvoiceChanges,
  summarizeChanges,
} from "../../utils/billingAuditDiff";
import {
  calculateSpecialFundAmount,
  formatSpecialFundInvoiceDescription,
} from "../../utils/accountingSpecialFund";

const PDF_PREVIEW_DEBOUNCE_MS = 400;

const REQUIRED_FIELD_MESSAGE = "This field is required.";
const DISCOUNT_OPTIONS = [0, 5, 10, 15, 20, 25];

const INVOICE_KIND_LABELS: Record<string, string> = {
  membershipDues: "Membership Dues",
  assessment: "Assessment",
  special: "Assessment",
  annualMeeting: "Annual Meeting",
  lateFee: "Late Fee",
  contingency: "Contingency",
  event: "Event",
  other: "Other",
};

type EditInvoiceModalProps = {
  office: AccountingOfficeDetail;
  invoice: BillingHistoryDoc | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type InvoiceTab = "annual" | "assessment";

type AnnualFieldErrors = {
  processingDate: boolean;
  billingPeriod: boolean;
  billingCriteria: boolean;
  discountAmount: boolean;
  specialFund: boolean;
};

const EMPTY_ANNUAL_FIELD_ERRORS: AnnualFieldErrors = {
  processingDate: false,
  billingPeriod: false,
  billingCriteria: false,
  discountAmount: false,
  specialFund: false,
};

function num(value: string | number | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatSignedMoney(value: number): string {
  const amount = Math.abs(Number(value) || 0);
  const formatted = formatMoney(amount);
  return value < 0 ? `-${formatted}` : formatted;
}

function tierLabel(t: DuesTier, isInvestmentBanking: boolean): string {
  const label =
    t.label ?? (isInvestmentBanking ? t.range : `${t.range} Professionals`);
  return `${label} (${formatMoney(t.fee)})`;
}

function clampToTodayOrBefore(iso: string): string {
  const today = todayIso();
  return iso > today ? today : iso;
}

function isAssessmentKind(kind?: string): boolean {
  return kind === "assessment" || kind === "special";
}

function parseDiscountPct(description?: string): number {
  const match = /\((\d+(?:\.\d+)?)%\)/.exec(description ?? "");
  return match ? Number(match[1]) : 0;
}

type OriginalItem = {
  code?: string;
  description?: string;
  amount?: number;
  quantity?: number;
  paid?: number;
};

type PaidInvoiceItem = NewInvoiceItem & { paid: number };

/**
 * Rebuilds the edited item list while preserving the amount already paid per
 * concept. Unchanged concepts keep their paid amount; a concept whose price was
 * lowered has its paid capped to the new billed amount; newly added concepts
 * start unpaid. Matching is done by `code`, consuming duplicates (e.g. multiple
 * "other" custom charges) in order.
 */
function carryOverPaid(
  newItems: NewInvoiceItem[],
  originalItems: OriginalItem[],
): PaidInvoiceItem[] {
  const paidByCode = new Map<string, number[]>();
  for (const it of originalItems) {
    const code = it.code ?? "";
    if (!paidByCode.has(code)) {
      paidByCode.set(code, []);
    }
    paidByCode.get(code)!.push(num(it.paid));
  }

  return newItems.map((it) => {
    const code = it.code ?? "";
    const queue = paidByCode.get(code);
    const priorPaid = queue && queue.length ? (queue.shift() as number) : 0;
    const billed = num(it.amount) * (num(it.quantity) || 1);
    const paid = Math.min(Math.max(0, priorPaid), Math.max(0, billed));
    return { ...it, paid: Math.round(paid * 100) / 100 };
  });
}

function sumItemPaid(items: PaidInvoiceItem[]): number {
  return Math.round(items.reduce((s, it) => s + num(it.paid), 0) * 100) / 100;
}

function validateAnnualInvoiceFields(input: {
  processingDate: string;
  billingPeriod: string;
  tierRange: string;
  specialFund: string;
  feesLoading: boolean;
}): string | null {
  if (input.feesLoading) {
    return "Fee catalog is still loading. Please wait.";
  }
  if (!input.processingDate.trim()) {
    return "Processing Date is required.";
  }
  if (input.processingDate > todayIso()) {
    return "Processing Date cannot be in the future.";
  }
  if (!input.billingPeriod.trim()) {
    return "Billing Period is required.";
  }
  if (!input.tierRange.trim()) {
    return "Billing Criteria is required.";
  }
  if (input.specialFund.trim() === "") {
    return "Special Fund is required.";
  }
  return null;
}

export default function EditInvoiceModal({
  office,
  invoice,
  isOpen,
  onClose,
  onSaved,
}: EditInvoiceModalProps): React.ReactElement {
  const officeName = officeLegalName(office);
  const isSent = invoice?.isSent === true;
  const isCancelled = invoice ? isCancelledBillingDoc(invoice) : false;
  const isReadOnly = isSent || isCancelled;
  const kind = invoice?.context?.kind ?? "";
  const isAnnual = kind === "membershipDues";
  const isAssessment = isAssessmentKind(kind);
  const isAssessmentMergedIntoAnnual =
    isAssessment &&
    Boolean(
      invoice?.mergedWithAnnualInvoiceId?.trim() ||
        invoice?.mergedWithAnnualInvoiceNumber?.trim(),
    );
  const isAssessmentEditLocked = isAssessment && isAssessmentMergedIntoAnnual;
  const formLocked = isReadOnly || isAssessmentEditLocked;
  const { logEvent } = useInvoiceAuditLogger();
  const { loggedPerson, tokenDecoded } = useAuth();

  const [tab, setTab] = useState<InvoiceTab>("annual");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ── Annual fields ──────────────────────────────────────────────────────────
  const [processingDate, setProcessingDate] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("");
  const [tiers, setTiers] = useState<DuesTier[]>([]);
  const [isInvestmentBanking, setIsInvestmentBanking] = useState(false);
  const [feesLoading, setFeesLoading] = useState(false);
  const [tierRange, setTierRange] = useState("");
  const [savedAnnualDues, setSavedAnnualDues] = useState(0);
  const [lockedFiscalYear, setLockedFiscalYear] = useState<number | null>(null);
  const [discountPct, setDiscountPct] = useState(0);
  const [specialFund, setSpecialFund] = useState("0");
  const [specialFundName, setSpecialFundName] = useState("Special Fund");
  const [specialFundPercentage, setSpecialFundPercentage] = useState(10);
  const [annualMeetingFee, setAnnualMeetingFee] = useState("0");
  const [includeBalance, setIncludeBalance] = useState(false);
  const [balance, setBalance] = useState("0");
  const [lateFees, setLateFees] = useState("0");
  const [includeInitiation, setIncludeInitiation] = useState(false);
  const [initiationFee, setInitiationFee] = useState("0");
  const [includeAssessment, setIncludeAssessment] = useState(false);
  const [assessmentFee, setAssessmentFee] = useState("0");
  const [addNote, setAddNote] = useState(false);
  const [note, setNote] = useState("");

  const [officeBalance, setOfficeBalance] = useState(0);

  const [processingDateError, setProcessingDateError] = useState(false);
  const [billingPeriodError, setBillingPeriodError] = useState(false);
  const [billingCriteriaError, setBillingCriteriaError] = useState(false);
  const [specialFundError, setSpecialFundError] = useState(false);
  const [annualFieldErrors, setAnnualFieldErrors] = useState<AnnualFieldErrors>(
    EMPTY_ANNUAL_FIELD_ERRORS,
  );

  // ── Assessment fields ──────────────────────────────────────────────────────
  const [assessmentDate, setAssessmentDate] = useState("");
  const [baseAssessment, setBaseAssessment] = useState("0");
  const [addCustomCharges, setAddCustomCharges] = useState(false);
  const [customCharges, setCustomCharges] = useState<
    { description: string; amount: string }[]
  >([{ description: "", amount: "0" }]);

  // ── Shared ─────────────────────────────────────────────────────────────────
  const [memo, setMemo] = useState("");
  const [coverLetterBody, setCoverLetterBody] = useState("");
  const [coverLetterDirty, setCoverLetterDirty] = useState(false);

  // ── PDF preview ────────────────────────────────────────────────────────────
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState("");

  // The balance effect this invoice originally applied to the office balance
  // (credit consumed increases the office balance toward 0; debt charged
  // decreases it). Needed to compute caps and the delta on save.
  const originalCreditApplied = useMemo(
    () => num(invoice?.creditApplied),
    [invoice],
  );
  const originalDebt = useMemo(
    () =>
      num(
        invoice?.items?.find((i) => i.code === "previousBalance")?.amount,
      ),
    [invoice],
  );
  const originalBalanceEffect = originalCreditApplied - originalDebt;

  const applyFeesFromCatalog = useCallback(
    (
      fees: Awaited<ReturnType<typeof getAccountingFeesForYear>>,
      preferredTierRange?: string,
      options?: {
        preserveHydratedAmounts?: boolean;
        savedAnnualAmount?: number;
      },
    ) => {
      const savedAnnualAmount = options?.savedAnnualAmount ?? 0;
      let duesTiers = [...fees.duesTiers];

      let range = preferredTierRange ?? "";
      if (
        range &&
        !duesTiers.some((tier) => tier.range === range) &&
        savedAnnualAmount > 0
      ) {
        range = savedInvoiceTierRangeId(savedAnnualAmount);
        duesTiers = [
          ...duesTiers,
          {
            range,
            fee: savedAnnualAmount,
            label: `Invoiced amount (${formatMoney(savedAnnualAmount)})`,
          },
        ];
      } else if (!range && savedAnnualAmount > 0) {
        const matchedTier = duesTiers.find(
          (tier) => tier.fee === savedAnnualAmount,
        );
        if (matchedTier) {
          range = matchedTier.range;
        } else {
          range = savedInvoiceTierRangeId(savedAnnualAmount);
          duesTiers = [
            ...duesTiers,
            {
              range,
              fee: savedAnnualAmount,
              label: `Invoiced amount (${formatMoney(savedAnnualAmount)})`,
            },
          ];
        }
      } else if (!range) {
        range = fees.defaultTierRange || duesTiers[0]?.range || "";
      }

      setTiers(duesTiers);
      setIsInvestmentBanking(fees.isInvestmentBanking);
      setSpecialFundName(fees.specialFundName);
      setSpecialFundPercentage(fees.specialFundPercentage);
      setTierRange(range);

      const catalogTierRange = resolveCatalogTierRangeForFees(
        range,
        fees.duesTiers,
        savedAnnualAmount,
      );
      setInitiationFee(
        String(fees.initiationByRange[catalogTierRange] ?? 0),
      );

      if (!options?.preserveHydratedAmounts) {
        const tier = duesTiers.find((t) => t.range === range);
        const base = tier?.fee ?? 0;
        setSpecialFund(
          calculateSpecialFundAmount(
            base,
            fees.specialFundPercentage,
          ).toString(),
        );
      }
      setBillingCriteriaError(false);
      setSpecialFundError(false);
    },
    [],
  );

  // ── Init state from invoice on open ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !invoice) {
      setLockedFiscalYear(null);
      setSavedAnnualDues(0);
      return;
    }
    setTab("annual");
    setSaving(false);
    setError("");
    setPdfError("");
    setProcessingDateError(false);
    setBillingPeriodError(false);
    setBillingCriteriaError(false);
    setSpecialFundError(false);
    setAnnualFieldErrors(EMPTY_ANNUAL_FIELD_ERRORS);

    const items = invoice.items ?? [];
    const findAmount = (code: string) =>
      String(items.find((i) => i.code === code)?.amount ?? 0);

    if (isAnnual) {
      const annualDues = num(items.find((i) => i.code === "annual")?.amount);
      setSavedAnnualDues(annualDues);
      const savedFiscalYear = invoice.context?.fiscalYear;
      setLockedFiscalYear(
        savedFiscalYear != null && Number.isFinite(Number(savedFiscalYear))
          ? Number(savedFiscalYear)
          : fiscalYearFromIso(
              invoice.dueDate ?? invoice.issueDate ?? todayIso(),
            ),
      );
      setProcessingDate(invoice.issueDate ?? todayIso());
      setBillingPeriod(invoice.dueDate ?? invoice.issueDate ?? todayIso());
      setDiscountPct(
        parseDiscountPct(items.find((i) => i.code === "discount")?.description),
      );
      setSpecialFund(findAmount("special"));
      setAnnualMeetingFee(findAmount("annualMeeting"));
      setLateFees(findAmount("late"));
      const initiationItem = items.find((i) => i.code === "initiation");
      setIncludeInitiation(!!initiationItem);
      const assessmentItem = items.find((i) => i.code === "assessment");
      setIncludeAssessment(!!assessmentItem);
      setAssessmentFee(String(assessmentItem?.amount ?? 0));
      setBaseAssessment(String(assessmentItem?.amount ?? 0));
      setAssessmentDate(invoice.issueDate ?? todayIso());
      setAddCustomCharges(false);
      setCustomCharges([{ description: "", amount: "0" }]);

      if (originalCreditApplied > 0) {
        setIncludeBalance(true);
        setBalance(String(originalCreditApplied));
      } else if (originalDebt > 0) {
        setIncludeBalance(true);
        setBalance(String(originalDebt));
      } else {
        setIncludeBalance(false);
        setBalance("0");
      }

      const hasMemo = !!(invoice.memo ?? "").trim();
      setAddNote(hasMemo);
      setNote(invoice.memo ?? "");
    } else if (isAssessment) {
      setAssessmentDate(invoice.issueDate ?? todayIso());
      setBaseAssessment(findAmount("assessment"));
      const others = items
        .filter((i) => i.code === "other")
        .map((i) => ({
          description: i.description ?? "",
          amount: String(i.amount ?? 0),
        }));
      if (others.length > 0) {
        setAddCustomCharges(true);
        setCustomCharges(others);
      } else {
        setAddCustomCharges(false);
        setCustomCharges([{ description: "", amount: "0" }]);
      }
      setMemo(invoice.memo ?? "");
    } else {
      setMemo(invoice.memo ?? "");
    }

    const savedCoverLetter = (invoice.coverLetterBody ?? "").trim();
    setCoverLetterDirty(false);
    if (savedCoverLetter) {
      setCoverLetterBody(savedCoverLetter);
    } else {
      const subject = buildAccountingInvoiceSectionTitle(invoice);
      setCoverLetterBody(
        buildDefaultCoverLetterBody(subject, officeLegalName(office)),
      );
    }
  }, [
    isOpen,
    invoice,
    isAnnual,
    isAssessment,
    originalCreditApplied,
    originalDebt,
    office.billingStartDate,
    office,
  ]);

  // ── Refresh office balance on open ────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      const raw = await getOfficeById(office.id);
      if (cancelled) {
        return;
      }
      setOfficeBalance(Number((raw?.balance ?? office.balance) ?? 0));
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, office.id, office.balance]);

  // ── Load fee catalog (annual only), preserving the invoice's tier ─────────
  useEffect(() => {
    if (!isOpen || !isAnnual || !invoice || lockedFiscalYear == null) {
      return;
    }
    let cancelled = false;
    setFeesLoading(true);
    (async () => {
      try {
        const officeForFees =
          (await getAccountingOfficeDetail(office.id)) ?? office;
        const fees = await getAccountingFeesForYear(
          lockedFiscalYear,
          toFeeContext(officeForFees),
        );
        if (cancelled) {
          return;
        }
        const annualDues = num(
          invoice.items?.find((i) => i.code === "annual")?.amount,
        );
        const matchedTier = fees.duesTiers.find((t) => t.fee === annualDues);
        applyFeesFromCatalog(fees, matchedTier?.range, {
          preserveHydratedAmounts: true,
          savedAnnualAmount: annualDues,
        });
      } catch {
        if (!cancelled) {
          setAnnualFieldErrors((prev) => ({
            ...prev,
            billingCriteria: true,
            discountAmount: true,
          }));
        }
      } finally {
        if (!cancelled) {
          setFeesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    isAnnual,
    lockedFiscalYear,
    office,
    invoice,
    applyFeesFromCatalog,
  ]);

  // ── Derived annual values ─────────────────────────────────────────────────
  // The fetched office balance already reflects this invoice's original
  // credit/debt application. Reverse it to recover the balance available as if
  // this invoice had not been applied, so caps/initial values stay consistent.
  const officeBalanceForCaps = officeBalance - originalBalanceEffect;
  const availableCredit = Math.max(0, -officeBalanceForCaps);
  const availableDebt = Math.max(0, officeBalanceForCaps);
  const creditIsDebt = officeBalanceForCaps > 0;
  const hasBalanceToApply = availableCredit > 0 || availableDebt > 0;

  const tierOptions = useMemo(
    () =>
      tiers.map((t) => ({
        id: t.range,
        name: tierLabel(t, isInvestmentBanking),
      })),
    [tiers, isInvestmentBanking],
  );
  const discountOptions = useMemo(
    () =>
      DISCOUNT_OPTIONS.map((p) => ({
        id: String(p),
        name: p === 0 ? "No Discount" : `${p}%`,
      })),
    [],
  );
  const selectedTierName =
    tierOptions.find((t) => t.id === tierRange)?.name ?? "";
  const selectedDiscountName =
    discountOptions.find((o) => Number(o.id) === discountPct)?.name ??
    "No Discount";

  const baseDues = useMemo(() => {
    const savedTierFee = feeFromSavedInvoiceTierRange(tierRange);
    if (savedTierFee != null) {
      return savedTierFee;
    }
    const catalogFee = tiers.find((t) => t.range === tierRange)?.fee;
    if (catalogFee != null) {
      return catalogFee;
    }
    return savedAnnualDues;
  }, [tiers, tierRange, savedAnnualDues]);
  const discountAmount = useMemo(
    () => Math.round(baseDues * (discountPct / 100) * 100) / 100,
    [baseDues, discountPct],
  );
  const netDues = Math.max(0, baseDues - discountAmount);
  const annualMeetingAmount = num(annualMeetingFee);
  const lateFeesAmount = num(lateFees);
  const initiationAmount = includeInitiation ? num(initiationFee) : 0;
  const assessmentAmount = includeAssessment ? num(assessmentFee) : 0;
  const showAnnualAssessmentTabs = isAnnual && includeAssessment;
  const isAssessmentMergedWithAnnual = showAnnualAssessmentTabs;

  const mergedAnnualInvoiceRef = useMemo(
    () => invoice?.invoiceNumber ?? invoice?.id ?? "Pending",
    [invoice?.invoiceNumber, invoice?.id],
  );

  const mergedAnnualInvoiceDate = useMemo(
    () => isoToDisplay(processingDate),
    [processingDate],
  );

  useEffect(() => {
    if (!isAssessmentMergedWithAnnual) {
      return;
    }
    setBaseAssessment(assessmentFee);
    setAssessmentDate(processingDate);
    setAddCustomCharges(false);
  }, [isAssessmentMergedWithAnnual, assessmentFee, processingDate]);

  const customChargesTotal = useMemo(
    () =>
      addCustomCharges
        ? customCharges.reduce((s, c) => s + num(c.amount), 0)
        : 0,
    [addCustomCharges, customCharges],
  );
  const assessmentTotal = num(baseAssessment) + customChargesTotal;

  const annualSubtotal = useMemo(() => {
    return (
      netDues +
      num(specialFund) +
      annualMeetingAmount +
      lateFeesAmount +
      initiationAmount +
      assessmentAmount
    );
  }, [
    netDues,
    specialFund,
    annualMeetingAmount,
    lateFeesAmount,
    initiationAmount,
    assessmentAmount,
  ]);

  const maxBalanceApplicable = creditIsDebt
    ? availableDebt
    : Math.max(0, Math.min(availableCredit, annualSubtotal));

  const appliedBalance = includeBalance
    ? Math.max(0, Math.min(num(balance), maxBalanceApplicable))
    : 0;
  const appliedCredit = creditIsDebt ? 0 : appliedBalance;
  const appliedDebt = creditIsDebt ? appliedBalance : 0;

  const annualTotal = useMemo(
    () => Math.round((annualSubtotal + appliedDebt) * 100) / 100,
    [annualSubtotal, appliedDebt],
  );
  const netDue = annualTotal - appliedCredit;

  const handleCreditBalanceChange = (raw: string) => {
    if (raw.trim() === "") {
      setBalance("");
      return;
    }
    const clamped = Math.max(0, Math.min(num(raw), maxBalanceApplicable));
    setBalance(String(clamped));
  };

  useEffect(() => {
    if (maxBalanceApplicable <= 0) {
      return;
    }
    if (num(balance) > maxBalanceApplicable) {
      setBalance(String(maxBalanceApplicable));
    }
  }, [balance, maxBalanceApplicable]);

  // ── Item builders ─────────────────────────────────────────────────────────
  const buildAnnualItems = useCallback((): NewInvoiceItem[] => {
    const items: NewInvoiceItem[] = [];
    if (baseDues > 0) {
      items.push({
        code: "annual",
        description: "Annual membership dues",
        amount: baseDues,
      });
    }
    if (discountAmount > 0) {
      items.push({
        code: "discount",
        description: `Member discount (${discountPct}%)`,
        amount: -discountAmount,
      });
    }
    if (num(specialFund) > 0) {
      items.push({
        code: "special",
        description: formatSpecialFundInvoiceDescription({
          specialFundName,
          specialFundPercentage,
        }),
        amount: num(specialFund),
      });
    }
    if (annualMeetingAmount > 0) {
      items.push({
        code: "annualMeeting",
        description: "Annual meeting registration fee",
        amount: annualMeetingAmount,
      });
    }
    if (lateFeesAmount > 0) {
      items.push({
        code: "late",
        description: "Late fees",
        amount: lateFeesAmount,
      });
    }
    if (initiationAmount > 0) {
      items.push({
        code: "initiation",
        description: "Initiation fee",
        amount: initiationAmount,
      });
    }
    if (assessmentAmount > 0) {
      items.push({
        code: "assessment",
        description: "Assessment fee",
        amount: assessmentAmount,
      });
    }
    if (appliedDebt > 0) {
      items.push({
        code: "previousBalance",
        description: MEMBERSHIP_BALANCE_LABEL,
        amount: appliedDebt,
      });
    }
    return items;
  }, [
    baseDues,
    discountAmount,
    discountPct,
    specialFund,
    specialFundName,
    specialFundPercentage,
    annualMeetingAmount,
    lateFeesAmount,
    initiationAmount,
    assessmentAmount,
    appliedDebt,
  ]);

  const buildAssessmentItems = useCallback((): NewInvoiceItem[] => {
    const baseItems: NewInvoiceItem[] =
      num(baseAssessment) > 0
        ? [
            {
              code: "assessment",
              description: "Base assessment",
              amount: num(baseAssessment),
            },
          ]
        : [];
    const customItems: NewInvoiceItem[] = addCustomCharges
      ? customCharges
          .filter((c) => num(c.amount) > 0)
          .map((c) => ({
            code: "other",
            description: c.description.trim() || "Custom charge",
            amount: num(c.amount),
          }))
      : [];
    return [...baseItems, ...customItems];
  }, [baseAssessment, addCustomCharges, customCharges]);

  // ── Build preview doc from current state ──────────────────────────────────
  const previewInvoice = useMemo((): BillingHistoryDoc | null => {
    if (!invoice) {
      return null;
    }

    const originalItems = (invoice.items ?? []) as OriginalItem[];
    const hadItemPaid = originalItems.some((it) => it.paid != null);

    if (isAnnual) {
      const built = buildAnnualItems();
      const items = hadItemPaid ? carryOverPaid(built, originalItems) : built;
      const total =
        Math.round(items.reduce((s, i) => s + num(i.amount), 0) * 100) / 100;
      // When the invoice tracks paid amounts per concept, derive totalPaid from
      // the (preserved) per-item paid so paid concepts stay paid and only the
      // edited/added concept changes. Otherwise fall back to preserving the
      // non-credit payments recorded at the invoice level.
      let totalPaid: number;
      if (hadItemPaid) {
        totalPaid = sumItemPaid(items as PaidInvoiceItem[]);
      } else {
        const realPayments = num(invoice.totalPaid) - originalCreditApplied;
        totalPaid =
          Math.round((Math.max(0, realPayments) + appliedCredit) * 100) / 100;
      }
      return {
        ...invoice,
        issueDate: processingDate || invoice.issueDate,
        dueDate: billingPeriod || invoice.dueDate,
        items,
        total,
        totalPaid,
        balance: Math.round((total - totalPaid) * 100) / 100,
        status: deriveBillingStatus(total, totalPaid),
        creditApplied: appliedCredit,
        memo: addNote && note.trim() ? note.trim() : undefined,
        coverLetterBody: coverLetterBody.trim() || undefined,
      };
    }

    if (isAssessment) {
      const built = buildAssessmentItems();
      const newItems = hadItemPaid ? carryOverPaid(built, originalItems) : built;
      const total =
        Math.round(newItems.reduce((s, i) => s + num(i.amount), 0) * 100) / 100;
      const totalPaid = hadItemPaid
        ? sumItemPaid(newItems as PaidInvoiceItem[])
        : num(invoice.totalPaid);
      return {
        ...invoice,
        issueDate: assessmentDate || invoice.issueDate,
        items: newItems,
        total,
        totalPaid,
        balance: Math.round((total - totalPaid) * 100) / 100,
        status: deriveBillingStatus(total, totalPaid),
        memo: memo.trim() || undefined,
        coverLetterBody: coverLetterBody.trim() || undefined,
      };
    }

    // Other kinds: only memo is editable
    return {
      ...invoice,
      memo: memo.trim() || undefined,
      coverLetterBody: coverLetterBody.trim() || undefined,
    };
  }, [
    invoice,
    isAnnual,
    isAssessment,
    buildAnnualItems,
    buildAssessmentItems,
    processingDate,
    billingPeriod,
    appliedCredit,
    originalCreditApplied,
    addNote,
    note,
    assessmentDate,
    memo,
    coverLetterBody,
  ]);

  useEffect(() => {
    if (!isOpen || !previewInvoice || coverLetterDirty) {
      return;
    }
    if ((invoice?.coverLetterBody ?? "").trim()) {
      return;
    }
    const subject = buildAccountingInvoiceSectionTitle(previewInvoice);
    setCoverLetterBody(
      buildDefaultCoverLetterBody(subject, officeLegalName(office)),
    );
  }, [isOpen, previewInvoice, coverLetterDirty, invoice?.coverLetterBody, office]);

  // ── PDF regeneration ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !previewInvoice) {
      setPdfBlob(null);
      setPdfGenerating(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setPdfGenerating(true);
        setPdfError("");
        try {
          const blob = await generateAccountingInvoicePDFBlob({
            invoice: previewInvoice,
            office,
            memoOverride: previewInvoice.memo,
          });
          if (cancelled) {
            return;
          }
          setPdfBlob(blob);
        } catch {
          if (!cancelled) {
            setPdfError("Unable to load invoice PDF preview.");
            setPdfBlob(null);
          }
        } finally {
          if (!cancelled) {
            setPdfGenerating(false);
          }
        }
      })();
    }, PDF_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, previewInvoice, office]);

  // ── Derived display values ─────────────────────────────────────────────────
  const kindLabel = INVOICE_KIND_LABELS[kind] ?? kind ?? "Invoice";

  const totalPaid = Number(invoice?.totalPaid) || 0;
  const creditApplied = Number(invoice?.creditApplied) || 0;
  const previewTotal = previewInvoice?.total ?? Number(invoice?.total) ?? 0;
  const previewBalance = previewInvoice?.balance ?? previewTotal - totalPaid;

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!invoice || !previewInvoice) {
      return;
    }
    setError("");

    if (isAnnual) {
      const validationError = validateAnnualInvoiceFields({
        processingDate,
        billingPeriod,
        tierRange,
        specialFund,
        feesLoading,
      });
      if (validationError) {
        setError(validationError);
        setProcessingDateError(
          !processingDate.trim() || processingDate > todayIso(),
        );
        setBillingPeriodError(!billingPeriod.trim());
        setBillingCriteriaError(!tierRange.trim());
        setSpecialFundError(specialFund.trim() === "");
        return;
      }
      if ((previewInvoice.items ?? []).length === 0) {
        setError("Add at least one charge before saving.");
        return;
      }
    }

    setSaving(true);
    try {
      const updated: FirmInvoiceDoc = {
        ...(previewInvoice as FirmInvoiceDoc),
      };
      await upsertInvoice(updated);
      const changes = buildInvoiceChanges(invoice, updated);
      void logEvent({
        billingDocId: updated.id,
        billingDocType: "invoice",
        partyKey: updated.partyKey,
        partyId: office.id,
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        invoiceNumber: updated.invoiceNumber,
        contextKind: updated.context?.kind,
        action: "edited",
        changes,
        detail: summarizeChanges(changes),
      });

      // Apply only the delta between the new and the originally-applied balance
      // effect so the office balance is not double-counted on edit.
      if (isAnnual) {
        const newBalanceEffect = appliedCredit - appliedDebt;
        const delta = newBalanceEffect - originalBalanceEffect;
        if (delta !== 0) {
          const { performedById, performedByName } = resolvePerformedBy(
            loggedPerson,
            tokenDecoded,
          );
          await recordInvoiceBalanceDeltaMovement({
            officeId: office.id,
            firmId: String(office.firm ?? "") || null,
            invoiceId: updated.id,
            invoiceRef: updated.invoiceNumber ?? updated.id,
            appliedCredit,
            appliedDebt,
            balanceDelta: delta,
            performedById,
            performedByName,
          });
        }
      }

      onSaved();
      onClose();
    } catch {
      setError("Unable to save invoice changes. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [
    invoice,
    previewInvoice,
    isAnnual,
    processingDate,
    billingPeriod,
    tierRange,
    specialFund,
    feesLoading,
    appliedCredit,
    appliedDebt,
    originalBalanceEffect,
    office.id,
    office.firm,
    logEvent,
    onSaved,
    onClose,
  ]);

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Preview & Edit Invoice"
      compact
      compactBodyScrolls
      className="max-w-6xl"
    >
      <div className="flex flex-col gap-5">
        <AccountingModalFirmSubtitle>
          {officeName}
          {invoice?.invoiceNumber ? ` · ${invoice.invoiceNumber}` : ""}
        </AccountingModalFirmSubtitle>

        {isCancelled && (
          <div className="rounded border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-font">
            This invoice has been cancelled and cannot be edited.
          </div>
        )}

        {isSent && !isCancelled && (
          <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This invoice has been sent and cannot be edited.
          </div>
        )}

        {isAssessmentMergedIntoAnnual && !isAnnual && (
          <AccountingAssessmentMergedNotice />
        )}

        {/* ── Header metadata (always read-only) ── */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div className="min-w-0">
            <div className={FEE_TYPE_LABEL_CLASS}>Invoice Number</div>
            <div className={FIELD_VALUE_CLASS}>
              {invoice?.invoiceNumber ?? "—"}
            </div>
          </div>
          <div className="min-w-0">
            <div className={FEE_TYPE_LABEL_CLASS}>Invoice Type</div>
            <div className="mt-1 text-base font-semibold text-gray-font md:text-lg">
              {kindLabel}
            </div>
          </div>
          <div className="min-w-0">
            <div className={FEE_TYPE_LABEL_CLASS}>Fiscal Year</div>
            <div className={FIELD_VALUE_CLASS}>
              {lockedFiscalYear || invoice?.context?.fiscalYear || "—"}
            </div>
          </div>
          {!isAnnual && invoice?.dueDate && (
            <div className="min-w-0">
              <div className={FEE_TYPE_LABEL_CLASS}>Billing Period</div>
              <div className={FIELD_VALUE_CLASS}>
                {isoToDisplay(invoice.dueDate)}
              </div>
            </div>
          )}
        </div>

        {showAnnualAssessmentTabs && (
          <AccountingModalTabs
            tabs={[
              { key: "annual", label: "Annual Invoice" },
              { key: "assessment", label: "Assessments Invoice" },
            ]}
            active={tab}
            onChange={(key) => {
              setTab(key);
              setError("");
            }}
          />
        )}

        {/* ── Annual editable fields (mirrors Create) ── */}
        {isAnnual && (!showAnnualAssessmentTabs || tab === "annual") && (
          <div className="flex flex-col gap-5">
            {isAssessmentMergedWithAnnual && <AccountingAssessmentMergedNotice />}

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <CalendarInput
                title="Processing Date"
                value={isoToDisplay(processingDate)}
                defaultValue={isoToDisplay(processingDate)}
                onChange={(date: string) => {
                  setProcessingDate(clampToTodayOrBefore(displayToIso(date)));
                  setProcessingDateError(false);
                }}
                type="single"
                required
                maxDate={new Date()}
                disabled={isReadOnly}
                error={processingDateError}
              />
              <CalendarInput
                title="Billing Period"
                value={isoToDisplay(billingPeriod)}
                defaultValue={isoToDisplay(billingPeriod)}
                onChange={() => {}}
                type="single"
                required
                disabled
                error={billingPeriodError}
              />

              <Dropdown
                title="Billing Criteria"
                data={tierOptions}
                selectedObjst={selectedTierName}
                getid
                disableSort
                required
                disabled
                disableInput
                seter={() => {}}
                error={billingCriteriaError}
              />
              <div className="flex flex-col gap-4">
                <Dropdown
                  title="Member Discount (%)"
                  data={discountOptions}
                  selectedObjst={selectedDiscountName}
                  getid
                  disableSort
                  disabled={isReadOnly}
                  seter={(selected: { id?: string } | null) => {
                    setDiscountPct(Number(selected?.id ?? 0));
                  }}
                  error={false}
                />
                <Input
                  title="Discount Amount"
                  value={formatMoney(discountAmount)}
                  required
                  disabled
                  error={annualFieldErrors.discountAmount}
                  errorMsj={
                    annualFieldErrors.discountAmount
                      ? REQUIRED_FIELD_MESSAGE
                      : undefined
                  }
                />
              </div>

              <Input
                title={`${specialFundName} (${specialFundPercentage}% Base)`}
                type="number"
                value={specialFund}
                required
                disabled
                error={specialFundError}
              />
              <Input
                title="Annual Meeting Reg. Fee"
                type="number"
                value={annualMeetingFee}
                onChange={(e) => setAnnualMeetingFee(e.target.value)}
                disabled={isReadOnly}
                error={false}
              />
              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeBalance}
                  onChange={setIncludeBalance}
                  label={
                    creditIsDebt
                      ? `Apply ${MEMBERSHIP_BALANCE_LABEL}`
                      : `Apply ${MEMBER_BALANCE_CREDIT_LABEL}`
                  }
                  disabled={isReadOnly || !hasBalanceToApply}
                />
                <Input
                  title={`${creditIsDebt ? MEMBERSHIP_BALANCE_LABEL : MEMBER_BALANCE_CREDIT_LABEL} (${formatSignedMoney(officeBalanceForCaps)})`}
                  type="number"
                  value={balance}
                  disabled={isReadOnly || !includeBalance}
                  onChange={(e) => handleCreditBalanceChange(e.target.value)}
                  error={false}
                />
                {creditIsDebt && (
                  <p className="text-xs text-gray-500">
                    Firm has an outstanding balance; the applied amount will be
                    added as an extra charge on this invoice.
                  </p>
                )}
              </div>
              <Input
                title="Late Fees"
                type="number"
                value={lateFees}
                onChange={(e) => setLateFees(e.target.value)}
                disabled={isReadOnly}
                error={false}
              />

              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeInitiation}
                  onChange={setIncludeInitiation}
                  label="Include Initiation Fee"
                  disabled={isReadOnly}
                />
                <Input
                  title="Initiation Fee"
                  type="number"
                  value={initiationFee}
                  disabled
                  error={false}
                />
              </div>
              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeAssessment}
                  onChange={setIncludeAssessment}
                  label="Include Assessment Fee"
                  disabled={isReadOnly}
                />
                <Input
                  title="Assessment Fee"
                  type="number"
                  value={assessmentFee}
                  onChange={(e) => setAssessmentFee(e.target.value)}
                  disabled={isReadOnly || !includeAssessment}
                  error={false}
                />
                {includeAssessment && (
                  <p className="text-xs text-gray-500">
                    This assessment amount will also be updated on the office&apos;s
                    assessment invoice.
                  </p>
                )}
              </div>
            </div>

            <AccountingModalSummaryPanel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AccountingModalSummaryLine
                  label="Annual Dues:"
                  value={formatMoney(baseDues)}
                />
                {discountAmount > 0 && (
                  <AccountingModalSummaryLine
                    label={`Member Discount (${discountPct}%):`}
                    value={`-${formatMoney(discountAmount)}`}
                  />
                )}
                <AccountingModalSummaryLine
                  label="Annual Dues (Net):"
                  value={formatMoney(netDues)}
                />
                <AccountingModalSummaryLine
                  label={`${specialFundName}:`}
                  value={formatMoney(num(specialFund))}
                />
                {annualMeetingAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Annual Meeting Reg. Fee:"
                    value={formatMoney(annualMeetingAmount)}
                  />
                )}
                {lateFeesAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Late Fees:"
                    value={formatMoney(lateFeesAmount)}
                  />
                )}
                {initiationAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Initiation Fee:"
                    value={formatMoney(initiationAmount)}
                  />
                )}
                {assessmentAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Assessment Fee:"
                    value={formatMoney(assessmentAmount)}
                  />
                )}
                {appliedDebt > 0 && (
                  <AccountingModalSummaryLine
                    label={`${MEMBERSHIP_BALANCE_LABEL}:`}
                    value={formatMoney(appliedDebt)}
                  />
                )}
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3">
                <AccountingModalSummaryLine
                  label="Total Annual Dues:"
                  value={formatMoney(annualTotal)}
                  strong
                />
                {appliedCredit > 0 && (
                  <>
                    <AccountingModalSummaryLine
                      label={`${MEMBER_BALANCE_CREDIT_LABEL}:`}
                      value={`-${formatMoney(appliedCredit)}`}
                    />
                    <AccountingModalSummaryLine
                      label="Total Due:"
                      value={formatMoney(netDue)}
                      strong
                    />
                  </>
                )}
              </div>
            </AccountingModalSummaryPanel>

            <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
              <AccountingModalCheckbox
                checked={addNote}
                onChange={setAddNote}
                label="Add Note (max 90 char. including space)"
                disabled={isReadOnly}
              />
              {addNote && (
                <Input
                  title="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 90))}
                  disabled={isReadOnly}
                  error={false}
                  placeholder="Note..."
                />
              )}
            </div>
          </div>
        )}

        {showAnnualAssessmentTabs && tab === "assessment" && (
          <div className="flex flex-col gap-5">
            <AccountingInvoiceMergeStatusNote
              annualInvoiceRef={mergedAnnualInvoiceRef}
              mergeDate={mergedAnnualInvoiceDate}
            />
            <div className="pointer-events-none flex flex-col gap-5 opacity-60">
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <CalendarInput
                  title="Date"
                  value={isoToDisplay(assessmentDate)}
                  defaultValue={isoToDisplay(assessmentDate)}
                  onChange={() => {}}
                  type="single"
                  disabled
                  error={false}
                />
                <Input
                  title="Base Assessment Amount"
                  type="number"
                  required
                  value={baseAssessment}
                  onChange={() => {}}
                  disabled
                  error={false}
                />
              </div>

              <div className="border-t border-gray-200 pt-3">
                <AccountingModalCheckbox
                  checked={addCustomCharges}
                  onChange={() => {}}
                  label="Add Custom Charges"
                  disabled
                />
              </div>

              <AccountingModalSummaryPanel>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AccountingModalSummaryLine
                    label="Base Assessment:"
                    value={formatMoney(num(baseAssessment))}
                  />
                  <AccountingModalSummaryLine
                    label="Custom Charges:"
                    value={formatMoney(customChargesTotal)}
                  />
                </div>
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <AccountingModalSummaryLine
                    label="Total Assessment Due:"
                    value={formatMoney(assessmentTotal)}
                    strong
                  />
                </div>
              </AccountingModalSummaryPanel>
            </div>
          </div>
        )}

        {/* ── Assessment editable fields ── */}
        {isAssessment && (
          <div className="flex flex-col gap-5">
            {isAssessmentMergedIntoAnnual && (
              <AccountingInvoiceMergeStatusNote
                annualInvoiceRef={
                  invoice?.mergedWithAnnualInvoiceNumber ??
                  invoice?.mergedWithAnnualInvoiceId ??
                  "Pending"
                }
                mergeDate={
                  invoice?.mergedAt
                    ? isoToDisplay(invoice.mergedAt)
                    : isoToDisplay(invoice?.issueDate ?? "")
                }
              />
            )}
            <div
              className={`flex flex-col gap-5 ${isAssessmentMergedIntoAnnual ? "pointer-events-none opacity-60" : ""}`}
            >
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <CalendarInput
                title="Date"
                value={isoToDisplay(assessmentDate)}
                defaultValue={isoToDisplay(assessmentDate)}
                onChange={(date: string) => setAssessmentDate(displayToIso(date))}
                type="single"
                disabled={isReadOnly || isAssessmentMergedIntoAnnual}
                error={false}
              />
              <Input
                title="Base Assessment Amount"
                type="number"
                required
                value={baseAssessment}
                onChange={(e) => setBaseAssessment(e.target.value)}
                disabled={isReadOnly || isAssessmentMergedIntoAnnual}
                error={false}
              />
            </div>

            <div className="border-t border-gray-200 pt-3">
              <AccountingModalCheckbox
                checked={addCustomCharges}
                onChange={setAddCustomCharges}
                label="Add Custom Charges"
                disabled={isReadOnly || isAssessmentMergedIntoAnnual}
              />
              {addCustomCharges && (
                <div className="mt-3 flex flex-col gap-3">
                  {customCharges.map((c, idx) => (
                    <div key={idx} className="flex items-end gap-3">
                      <div className="min-w-0 flex-1">
                        <Input
                          title="Description"
                          value={c.description}
                          placeholder="Description"
                          onChange={(e) =>
                            setCustomCharges((prev) =>
                              prev.map((p, i) =>
                                i === idx ? { ...p, description: e.target.value } : p,
                              ),
                            )
                          }
                          disabled={isReadOnly || isAssessmentMergedIntoAnnual}
                          error={false}
                        />
                      </div>
                      <div className="w-40 shrink-0">
                        <Input
                          title="Amount"
                          type="number"
                          value={c.amount}
                          onChange={(e) =>
                            setCustomCharges((prev) =>
                              prev.map((p, i) =>
                                i === idx ? { ...p, amount: e.target.value } : p,
                              ),
                            )
                          }
                          disabled={isReadOnly || isAssessmentMergedIntoAnnual}
                          error={false}
                        />
                      </div>
                      {!isReadOnly && !isAssessmentMergedIntoAnnual && (
                        <button
                          type="button"
                          className="pb-2 text-base text-primary hover:underline"
                          onClick={() =>
                            setCustomCharges((prev) =>
                              prev.length === 1 ? prev : prev.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  {!isReadOnly && !isAssessmentMergedIntoAnnual && (
                    <button
                      type="button"
                      className="self-start text-base font-medium text-primary hover:underline"
                      onClick={() =>
                        setCustomCharges((prev) => [
                          ...prev,
                          { description: "", amount: "0" },
                        ])
                      }
                    >
                      + Add charge
                    </button>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* ── Memo / Comments (non-annual invoice types) ── */}
        {!isAnnual && (
          <Input
            title="Comments"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={isReadOnly}
            error={false}
          />
        )}

        {/* ── Summary (non-annual) ── */}
        {!isAnnual && (
          <AccountingModalSummaryPanel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <AccountingModalSummaryLine
                label="Total"
                value={formatMoney(previewTotal)}
              />
              <AccountingModalSummaryLine
                label="Paid"
                value={formatMoney(totalPaid)}
              />
              <AccountingModalSummaryLine
                label="Balance"
                value={formatMoney(previewBalance)}
                strong
              />
              <AccountingModalSummaryLine
                label="Status"
                value={formatBillingStatusLabel(invoice?.status)}
              />
            </div>
          </AccountingModalSummaryPanel>
        )}

        {/* ── Line items table (read-only reference) ── */}
        {!isAnnual && !isAssessment && (
          <div>
            <AccountingModalSectionTitle>Line Items</AccountingModalSectionTitle>
            <div className="mt-3 table-responsive tabledata w-full overflow-x-auto">
              <table className="table min-w-full w-max">
                <thead>
                  <tr className="bg-accent-Default-dark text-white">
                    <th className="px-4 py-2 text-left text-xs font-medium">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium">Amount</th>
                    <th className="px-4 py-2 text-right text-xs font-medium">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice?.items ?? []).length === 0 && creditApplied <= 0 ? (
                    <tr className="text-gray-font text-xs">
                      <td className="px-4 py-3" colSpan={3}>No line items.</td>
                    </tr>
                  ) : (
                    <>
                      {(invoice?.items ?? []).map((item, index) => {
                        const paid = Number(item.paid) || 0;
                        return (
                          <tr
                            key={`${item.description ?? "item"}-${index}`}
                            className="even:bg-gray-100 text-gray-font text-xs"
                          >
                            <td className="px-4 py-2">{item.description ?? "—"}</td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {formatMoney(Number(item.amount) || 0)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {paid > 0 ? formatMoney(paid) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                      {creditApplied > 0 && (
                        <tr className="even:bg-gray-100 text-gray-font text-xs">
                          <td className="px-4 py-2">{MEMBER_BALANCE_CREDIT_LABEL}</td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            -{formatMoney(creditApplied)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {formatMoney(creditApplied)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── PDF Preview ── */}
        <div>
          <AccountingModalSectionTitle>Cover Letter</AccountingModalSectionTitle>
          <div className="mt-3">
            <TextArea
              title="Cover Letter Body"
              value={coverLetterBody}
              disabled={isReadOnly}
              onChange={(e) => {
                setCoverLetterDirty(true);
                setCoverLetterBody(e.target.value);
              }}
              height="10rem"
              error={false}
            />
          </div>
        </div>

        <div>
          <AccountingModalSectionTitle>Invoice PDF Preview</AccountingModalSectionTitle>
          <div className="mt-3 overflow-hidden rounded border border-gray-200 bg-gray-50">
            {pdfError ? (
              <div className="flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-red-600">
                {pdfError}
              </div>
            ) : (
              <InvoicePdfPreview
                pdfBlob={pdfBlob}
                loading={pdfGenerating}
                loadingMessage="Generating invoice preview..."
                emptyMessage="No preview available."
              />
            )}
          </div>
        </div>

        <AccountingModalError message={error} />

        {isReadOnly || isAssessmentEditLocked ? (
          <div className="flex justify-end border-t border-gray-200 pt-4">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={onClose}
            >
              CLOSE
            </button>
          </div>
        ) : (
          <AccountingModalFooter
            onCancel={onClose}
            onSubmit={handleSave}
            submitText="Save Changes"
            saving={saving}
            submitDisabled={
              tab === "assessment" &&
              (isAssessmentMergedWithAnnual || isAssessmentMergedIntoAnnual)
            }
          />
        )}
      </div>
    </Modal>
  );
}
